import { PostgreSQL } from '@codemirror/lang-sql';
import type { CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import type { Extension } from '@codemirror/state';

type SchemaMap = Record<string, string[]>;

/**
 * Parse FROM/JOIN clauses to extract table names and aliases.
 * Returns a Map<alias|name, tableName>.
 */
function parseFromClause(doc: string): Map<string, string> {
  const tables = new Map<string, string>();
  const re = /\b(?:from|join)\s+(\w+)(?:\s+(?:as\s+)?(\w+))?/gi;
  let m = re.exec(doc);
  while (m) {
    const table = m[1];
    const alias = m[2];
    tables.set(table, table);
    if (alias) tables.set(alias, table);
    m = re.exec(doc);
  }
  return tables;
}

/** SQL keywords that indicate a column context (boost columns higher). */
const COLUMN_CONTEXTS = new Set([
  'select', 'where', 'and', 'or', 'on', 'set', 'having',
]);

/** SQL keywords that indicate a table context (suppress column suggestions). */
const TABLE_CONTEXTS = new Set(['from', 'join', 'into', 'update']);

/**
 * Create a CodeMirror extension that provides column completions
 * based on FROM/JOIN tables and alias.column resolution.
 */
export function createColumnCompletion(schemaMap: SchemaMap): Extension {
  return PostgreSQL.language.data.of({
    autocomplete: (context: CompletionContext): CompletionResult | null => {
      const doc = context.state.doc.toString();
      const refs = parseFromClause(doc);
      if (refs.size === 0) return null;

      // Case 1: after "identifier." → resolve alias/table and suggest columns
      const dotMatch = context.matchBefore(/(\w+)\.\w*$/);
      if (dotMatch) {
        const prefix = dotMatch.text.split('.')[0];
        const table = refs.get(prefix);
        if (!table) return null;
        const cols = schemaMap[table];
        if (!cols) return null;
        const from = dotMatch.from + prefix.length + 1;
        return {
          from,
          options: cols.map(col => ({ label: col, type: 'property', detail: table, boost: 2 })),
        };
      }

      // Case 2: unqualified — suggest columns from referenced tables
      const word = context.matchBefore(/\w*/);
      if (!word) return null;
      if (word.from === word.to && !context.explicit) return null;

      const textBefore = doc.slice(0, word.from).toLowerCase();
      const lastKw = textBefore.match(
        /\b(select|from|join|where|and|or|on|set|order\s+by|group\s+by|having|update|into)\s*$/,
      );
      const ctx = lastKw?.[1]?.trim().toLowerCase();

      if (ctx && TABLE_CONTEXTS.has(ctx)) return null;

      const boost = ctx && COLUMN_CONTEXTS.has(ctx) ? 2 : 0;
      const seen = new Set<string>();
      const options: { label: string; type: string; detail: string; boost: number }[] = [];

      for (const [, table] of refs) {
        const cols = schemaMap[table];
        if (!cols) continue;
        for (const col of cols) {
          const key = `${table}.${col}`;
          if (seen.has(key)) continue;
          seen.add(key);
          options.push({ label: col, type: 'property', detail: table, boost });
        }
      }

      if (options.length === 0) return null;
      return { from: word.from, options };
    },
  });
}
